import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import Header from '../../../components/Header';
import { 
  ArrowLeft, ShieldCheck, Lock, FileText, Download, 
  AlertTriangle, Eye, User, Calendar, ShieldAlert 
} from 'lucide-react';

export default function VaultViewer() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  
  const [documents, setDocuments] = useState<any[]>([]);
  const [tenantName, setTenantName] = useState<string>('Locataire');
  const [loading, setLoading] = useState(true);
  const [accessLogged, setAccessLogged] = useState(false);

  useEffect(() => {
    if (tenantId) {
      fetchVaultData(tenantId);
    }
  }, [tenantId]);

  const fetchVaultData = async (id: string) => {
    try {
      // 1. Vérifier l'identité du proprio connecté
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login?type=habitat');
        return;
      }

      // 2. Récupérer le nom du locataire
      const { data: profile } = await supabase
        .from('habitat_profiles')
        .select('full_name')
        .eq('id', id)
        .maybeSingle();
      
      if (profile?.full_name) {
        setTenantName(profile.full_name);
      }

      // 3. Récupérer les documents (La politique RLS SQL bloque si le proprio n'a pas de candidature de ce locataire)
      const { data: docs, error } = await supabase
        .from('habitat_documents')
        .select('*')
        .eq('tenant_id', id);

      if (error) throw error;
      
      if (docs) {
        setDocuments(docs);
      }

      // Simulation d'un log d'accès (Conformité)
      setTimeout(() => setAccessLogged(true), 800);

    } catch (error) {
      console.error("Erreur de récupération du coffre:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDocTypeLabel = (type: string) => {
    switch(type) {
      case 'id_card': return "Pièce d'identité officielle";
      case 'income_proof': return "Preuve de revenus";
      case 'credit_report': return "Dossier de crédit";
      default: return "Document justificatif";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
        <Lock className="text-teal-500 w-16 h-16 mb-6 animate-pulse" />
        <h2 className="text-2xl font-black text-white mb-2 tracking-widest uppercase">Déchiffrement</h2>
        <p className="text-slate-400 font-bold">Ouverture du coffre-fort sécurisé...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-20 selection:bg-teal-500 selection:text-white">
      <Header />

      <main className="max-w-4xl mx-auto px-4 pt-32 pb-16">
        
        {/* Navigation retour */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 hover:text-teal-600 font-bold transition bg-white px-4 py-2 rounded-full border border-slate-200 shadow-sm">
            <ArrowLeft size={16} /> Retour au dossier
          </button>
        </div>

        {/* En-tête sécurisé */}
        <div className="bg-slate-900 rounded-[2.5rem] p-8 md:p-12 text-white shadow-2xl relative overflow-hidden mb-10 border border-slate-800">
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-[80px] pointer-events-none"></div>
          
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-teal-500/20 border border-teal-500/50 rounded-2xl flex items-center justify-center shrink-0">
                <ShieldCheck className="text-teal-400 w-10 h-10" />
              </div>
              <div>
                <p className="text-teal-400 font-black text-[10px] uppercase tracking-widest mb-1 flex items-center gap-2">
                  <Lock size={12} /> Espace Protégé Loi 25
                </p>
                <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">Coffre-fort Numérique</h1>
                <p className="text-slate-400 font-medium flex items-center gap-2">
                  <User size={16} /> Documents de : <strong className="text-white">{tenantName}</strong>
                </p>
              </div>
            </div>
            
            {accessLogged && (
              <div className="bg-slate-800/50 border border-slate-700 p-4 rounded-2xl shrink-0">
                <p className="text-xs text-slate-400 font-bold mb-1">Accès enregistré le</p>
                <p className="text-sm font-black text-white flex items-center gap-2"><Calendar size={14} className="text-teal-500"/> {new Date().toLocaleDateString('fr-CA')} à {new Date().toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            )}
          </div>
        </div>

        {/* Avertissement de confidentialité */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-10 flex gap-4 items-start shadow-sm">
          <ShieldAlert className="text-amber-500 shrink-0 w-6 h-6 mt-1" />
          <div>
            <h3 className="font-black text-amber-900 mb-1">Avis de Confidentialité (Loi 25)</h3>
            <p className="text-sm text-amber-800/80 leading-relaxed font-medium">
              Ces documents contiennent des informations personnelles sensibles. En vertu de la Loi 25 du Québec, vous êtes tenu de ne les utiliser <strong>que pour l'évaluation de cette candidature</strong>. Ils ne doivent pas être partagés ou conservés au-delà de la finalité prévue.
            </p>
          </div>
        </div>

        {/* Grille des documents */}
        <h3 className="text-2xl font-black text-slate-900 mb-6">Pièces justificatives</h3>
        
        {documents.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] p-16 text-center shadow-sm">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText size={32} className="text-slate-300" />
            </div>
            <h3 className="text-xl font-black text-slate-800 mb-2">Coffre vide</h3>
            <p className="text-slate-500 font-medium">Ce candidat n'a pas encore téléversé ses documents dans son coffre-fort.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {documents.map((doc) => (
              <div key={doc.id} className="bg-white rounded-[2rem] border border-slate-200 p-8 shadow-sm hover:shadow-xl transition-all group flex flex-col h-full">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center border border-teal-100 group-hover:scale-110 transition-transform">
                    <FileText size={24} />
                  </div>
                  <span className="bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
                    Sécurisé
                  </span>
                </div>
                
                <h4 className="text-xl font-black text-slate-900 mb-2">
                  {getDocTypeLabel(doc.document_type)}
                </h4>
                <p className="text-xs text-slate-400 font-bold mb-8 truncate" title={doc.file_name}>
                  {doc.file_name || 'Document attaché'}
                </p>

                <div className="mt-auto flex gap-3">
                  <a 
                    href={doc.file_url} 
                    target="_blank" 
                    rel="noreferrer"
                    className="flex-1 bg-slate-900 text-white py-4 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-teal-600 transition-colors flex items-center justify-center gap-2 shadow-lg"
                  >
                    <Eye size={16} /> Visualiser
                  </a>
                  <a 
                    href={doc.file_url} 
                    download
                    className="w-14 bg-white border border-slate-200 text-slate-600 py-4 rounded-xl hover:bg-slate-50 hover:text-teal-600 transition-colors flex items-center justify-center"
                    title="Télécharger"
                  >
                    <Download size={18} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

      </main>
    </div>
  );
}
